<script lang="ts">
  import { applyUpdateNow, isIos, isStandalone, promptInstall, pwa } from '../../pwa.svelte'
  import { layout } from '../../state/layout.svelte'
  import { places } from '../../state/places.svelte'
  import { read, write } from '../../storage'
  import Icon from '../Icon.svelte'

  /**
   * One toast at a time, outlined in ink (round 2's 3.7), under the map's
   * top-left controls. The handoff puts it bottom-left and above the sheet,
   * which is where the legend and slider card lives whenever a layer is on,
   * so it hangs where the popups already do instead. Two kinds, the update
   * first because it is the one with a clock on it:
   *
   * * **An update is ready.** A new build still applies itself when the page
   *   is hidden, untouched or idle five minutes (Phase 9); the toast is for
   *   somebody using the page who would rather have it now. Later hides it
   *   for this page's life, and the quiet path still runs.
   * * **Install.** Offered only once a place is saved, because that is when
   *   alerts become something to want, and only where installing is
   *   possible: Chromium's own prompt, or the Share sheet on an iPhone,
   *   where Web Push reaches the installed app alone. Dismissed once, it
   *   stays dismissed on this device.
   */

  const KEY_INSTALL = 'weather.installOffer'

  let later = $state(false)
  let installDismissed = $state(read(KEY_INSTALL) === 'dismissed')

  let iosTab = $derived(isIos() && !isStandalone())

  let kind = $derived.by((): 'update' | 'install' | 'ios' | null => {
    if (pwa.updateReady && !later) return 'update'
    if (installDismissed || places.list.length === 0) return null
    if (pwa.canInstall) return 'install'
    if (iosTab) return 'ios'
    return null
  })

  function dismissInstall() {
    installDismissed = true
    write(KEY_INSTALL, 'dismissed')
  }

  async function install() {
    await promptInstall()
    dismissInstall()
  }
</script>

{#if kind}
  <div
    class="toast"
    class:phone={!layout.isDesktop}
    role="region"
    aria-label={kind === 'update' ? 'Update ready' : 'Install Fusion WX'}
  >
    <span class="mark" aria-hidden="true">
      <Icon name={kind === 'update' ? 'refresh-cw' : 'download'} size={20} strokeWidth={2.5} />
    </span>
    <span class="body">
      {#if kind === 'update'}
        <span class="title">A new version of Fusion WX is ready</span>
        <span class="text">
          Reload to update. Your places and layers stay{layout.isDesktop ? ' as they are' : ''}.
        </span>
        <span class="actions">
          <button type="button" class="wx-btn ink touch-target" onclick={applyUpdateNow}>Reload</button>
          <button type="button" class="wx-btn quiet touch-target" onclick={() => (later = true)}>
            Later
          </button>
        </span>
      {:else if kind === 'install'}
        <span class="title">Install Fusion WX</span>
        <span class="text">It opens in its own window, and alerts arrive with the tab closed.</span>
        <span class="actions">
          <button type="button" class="wx-btn ink touch-target" onclick={() => void install()}>
            Install
          </button>
          <button type="button" class="wx-btn quiet touch-target" onclick={dismissInstall}>
            Not now
          </button>
        </span>
      {:else}
        <span class="title">Add to Home Screen</span>
        <span class="text">
          Tap Share in Safari, then Add to Home Screen. Alerts on iPhone need this.
        </span>
        <span class="actions">
          <button type="button" class="wx-btn ink touch-target" onclick={dismissInstall}>
            Got it
          </button>
        </span>
      {/if}
    </span>
  </div>
{/if}

<style>
  .toast {
    position: absolute;
    left: var(--chrome-left, 12px);
    top: calc(var(--controls-bottom, 52px) + 12px);
    z-index: 30;
    width: 400px;
    max-width: calc(100% - 32px);
    display: grid;
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 12px;
    padding: 16px 18px;
    background: var(--color-bg);
    color: var(--color-text);
    border: 2px solid var(--color-text);
    box-shadow: var(--shadow-lg);
  }

  /* Under the two floating buttons, full width less the gutters. */
  .toast.phone {
    left: 12px;
    right: 12px;
    top: calc(var(--controls-bottom, 0px) + 8px);
    width: auto;
    max-width: none;
    padding: 14px 16px;
  }

  .body {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .title {
    font-size: 15px;
    font-weight: 800;
  }

  .phone .title {
    font-size: 16px;
  }

  .text {
    font-size: 13px;
    line-height: 1.45;
  }

  .phone .text {
    font-size: 14px;
  }

  .actions {
    display: flex;
    gap: 8px;
    padding-top: 8px;
    --btn-h: 34px;
  }

  .phone .actions {
    --btn-h: 44px;
    --btn-fs: 15px;
  }
</style>
